import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class FormDataService {

  private readonly dataPath = '/assets/data';

  // Cached observables (lazily created on first subscribe, cleared on error so a failed
  // request doesn't stay cached for the lifetime of this singleton service)
  private businessTypes$: Observable<any[]>;
  private usStates$: Observable<any[]>;
  private rateTypes$: Observable<any[]>;

  // BehaviorSubjects for reactive updates
  private businessTypesSubject = new BehaviorSubject<any[]>([]);
  private usStatesSubject = new BehaviorSubject<any[]>([]);
  private rateTypesSubject = new BehaviorSubject<any[]>([]);

  constructor(private http: HttpClient) { }

  /**
   * Get business types as observable
   */
  getBusinessTypes(): Observable<any[]> {
    if (!this.businessTypes$) {
      this.businessTypes$ = this.http.get<any[]>(`${this.dataPath}/business-types.json`)
        .pipe(
          tap(data => this.businessTypesSubject.next(data)),
          shareReplay({ bufferSize: 1, refCount: true }),
          catchError(err => {
            this.businessTypes$ = null;
            return throwError(() => err);
          })
        );
    }
    return this.businessTypes$;
  }

  /**
   * Get business types as subject for reactive updates
   */
  getBusinessTypes$() {
    return this.businessTypesSubject.asObservable();
  }

  /**
   * Get US states as observable
   */
  getUsStates(): Observable<any[]> {
    if (!this.usStates$) {
      this.usStates$ = this.http.get<any[]>(`${this.dataPath}/us-states-simple.json`)
        .pipe(
          tap(data => this.usStatesSubject.next(data)),
          shareReplay({ bufferSize: 1, refCount: true }),
          catchError(err => {
            this.usStates$ = null;
            return throwError(() => err);
          })
        );
    }
    return this.usStates$;
  }

  /**
   * Get US states as subject for reactive updates
   */
  getUsStates$() {
    return this.usStatesSubject.asObservable();
  }

  /**
   * Get rate types as observable
   */
  getRateTypes(): Observable<any[]> {
    if (!this.rateTypes$) {
      this.rateTypes$ = this.http.get<any[]>(`${this.dataPath}/rate-types.json`)
        .pipe(
          tap(data => this.rateTypesSubject.next(data)),
          shareReplay({ bufferSize: 1, refCount: true }),
          catchError(err => {
            this.rateTypes$ = null;
            return throwError(() => err);
          })
        );
    }
    return this.rateTypes$;
  }

  /**
   * Get rate types as subject for reactive updates
   */
  getRateTypes$() {
    return this.rateTypesSubject.asObservable();
  }

  /**
   * Get business type by ID
   */
  getBusinessTypeById(id: number): Observable<any> {
    return new Observable(observer => {
      this.getBusinessTypes().subscribe(types => {
        const type = types.find(bt => bt.id === id);
        observer.next(type || null);
        observer.complete();
      });
    });
  }

  /**
   * Get state by code
   */
  getStateByCode(code: string): Observable<any> {
    return new Observable(observer => {
      this.getUsStates().subscribe(states => {
        const state = states.find(s => s.code === code);
        observer.next(state || null);
        observer.complete();
      });
    });
  }

  /**
   * Get rate type by code
   */
  getRateTypeByCode(code: string): Observable<any> {
    return new Observable(observer => {
      this.getRateTypes().subscribe(types => {
        const type = types.find(rt => rt.code === code);
        observer.next(type || null);
        observer.complete();
      });
    });
  }
}
